import { updateKeyInStore } from 'App';
import { store } from 'app/redux/store';
import { access } from 'fs';
import { flatten } from 'lodash';
import * as Yup from 'yup';
import { OptionalObjectSchema } from 'yup/lib/object';
import { requiredFileIf, requiredFiles } from '../../useValidateQuestionnaire';
import {
  OtherIncomeStateValues,
  TProperties,
  TProperty,
  updateFieldArrayKey,
  updateKey,
} from './slice';

export const fields: IBaseField<
  keyof OtherIncomeStateValues | keyof TProperty
>[] = [
  {
    dependenciesTitle: 'Corporations & Partnerships',
    name: 'doYouOwnAShareinterestInACorporationOrPartnershipwhetherUsOrForeign',
    label:
      'Do you own a percentage (Not Stock) in a Partnership or corporation whether US or foreign?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadSchK1OrForeignDocumentReportingYourShareOfIncomelosses',
        label:
          'Please upload Sch K-1 or foreign document reporting your share of income/losses',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'areYouTheSoleOwnerOfAUsLlc',
    label: 'Are you the sole owner of a US LLC?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadLlcRegistrationDocuments',
        label: 'Please upload LLC registration documents',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'whatIsTheEinOfTheLlc',
        label: 'What is the EIN of the LLC?',
        type: 'text',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Enter',
      },
      {
        name: 'doesTheLlcOwnUsRealProperty',
        label: 'Does the LLC own US real property?',
        type: 'radio',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'areThesePropertiesUsedForRentalPurposes',
            label: 'Are these properties used for rental purposes?',
            type: 'radio',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            dependencies: [
              {
                name: 'properties',
                label: 'Properties',
                type: 'fieldArray',
                predicate: (value) => value === 'yes',
                tooltip: '',
                comment: true,
                effect: (payload) =>
                  store.dispatch(updateFieldArrayKey(payload)),
                defaultDependency: {
                  addressOfProperty: null,
                  currentFairMarketValue: null,
                  purchasePrice: null,
                  purchaseDate: null,
                },
                dependencies: [
                  {
                    name: 'addressOfProperty',
                    label: 'Address of Property',
                    type: 'textarea',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                    placeholder: 'Enter',
                  },
                  {
                    name: 'currentFairMarketValue',
                    label: 'Current fair market value',
                    type: 'currency',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'purchasePrice',
                    label: 'Purchase Price',
                    type: 'currency',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                  {
                    name: 'purchaseDate',
                    label: 'Purchase Date',
                    type: 'currency',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'duringYearDidYouReceiveInheritanceOrGiftsFromANonUSPerson',
    label:
      'During the selected year, did you receive inheritance or gifts from a non-US person?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'duringYearDidYouReceiveInheritanceOrGiftsFromANonUSPersonProvideDetails',
        label: 'Please provide details',
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Enter',
      },
      {
        name: 'duringYearDidYouReceiveInheritanceOrGiftsFromANonUSPersonProvideDocument',
        label: 'Please provide details',
        type: 'upload',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
      },
    ],
  },
  {
    dependenciesTitle: 'Miscellaneous Income',
    name: 'didYouGiftAnyOnePersonOver$15k',
    label: 'Did you gift any one person over $15k?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'cryptocurrencyDidYouReceiveSellExchangeOrOtherwiseAcquireAnyFinancialInterestInAnyVirtualCurrencyieBitcoin',
    label:
      'Cryptocurrency: Did you receive, sell, exchange, or otherwise acquire any financial interest in any virtual currency (ie Bitcoin)',
    subLabel: () => (
      <p>Please reflect these transactions in the investments section.</p>
    ),
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'didYouReceiveAStipendOrFellowshipGrant',
    label: 'Did you receive a stipend or fellowship grant?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'payorNameAndAddress',
        label: 'Payor name and address',
        type: 'textarea',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Enter',
      },
      {
        name: 'amountOfAward',
        label: 'Amount of award',
        type: 'currency',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'uploadAwardLetter',
        label: 'Upload award letter',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'amountOfRelatedTravelAndTransportationExpensesifInResidenceFellow',
        label:
          'Amount of related travel and transportation expenses (if in-residence fellow)',
        type: 'currency',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'didYouReceiveRoyalitieseitherUsOrForeign',
    label: 'Did you receive royalities (either US or foreign)?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadForm1099MiscOrForeignDocumentReportingRoyaltiesReceived',
        label:
          'Please upload form 1099-MISC or foreign document reporting royalties received',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },

  {
    name: 'didYouReceiveARefundOrUnemploymentIncomeFromAUsStateInYear',
    label:
      'Did you receive a refund or unemployment income from a US state in the selected year?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadForm1099G',
        label: 'Please upload form 1099-G',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'didYouPayOrReceiveAlimonyDuringYear',
    label: 'Did you pay or receive Alimony during the selected year?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    options: [
      { label: 'N/A', value: 'N/A' },
      { label: 'Paid', value: 'Paid' },
      { label: 'Recieved', value: 'Recieved' },
    ],
    dependencies: [
      {
        name: 'wasTheAlimonyPaymentreceiptMadeUnderADivorceOrSeparationAgreementEnteredIntoOnOrBeforeDec312018',
        label:
          'Was the alimony payment/receipt made under a divorce or separation agreement entered into on or before Dec 31, {year}?',
        type: 'radio',
        predicate: (value) => value === 'Paid' || value === 'Recieved',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        // ATTENTION
        dependencies: [
          {
            name: 'amountAlimonyPaid',
            label: 'Annual Amount of Alimony',
            type: 'currency',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'recipientsName',
            label: "Recipient's Name",
            type: 'text',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'yes',
            placeholder: 'Enter',
          },
          {
            name: 'recipientSs',
            label: 'Recipient SS#',
            type: 'text',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'yes',
            placeholder: 'Enter',
          },
          {
            name: 'originalDivorceSeparationAgreementDate',
            label: 'Original divorce/separation agreement date',
            type: 'text',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'yes',
            placeholder: 'Enter',
          },
        ],
      },
    ],
  },

  {
    name: 'didYouReceiveNonUsMonetaryBenefitsOfAnyKind',
    label: 'Did you receive non-US monetary benefits of any kind?',
    subLabel: () => <p>Disability, maternity, paternity, unemployment</p>,
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'amountOfUnemploymentBenefitsYouReceived',
        label: 'Amount of benefits you received',
        type: 'currency',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },

      {
        name: 'pleaseSpecifyTheTypeOfBenefit',
        label: 'Please specify the type of benefit',
        type: 'select',
        predicate: (value) => value === 'yes',
        options: [
          { label: 'Disability', value: 'Disability' },
          { label: 'Maternity/Paternity', value: 'Maternity/Paternity' },
          { label: 'Unemployment', value: 'Unemployment' },
          { label: 'Other', value: 'Other' },
        ],
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'pleaseUploadSupportingDocuments',
        label: 'Please upload supporting documents if any',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'wereTheBenefitsProvidedByYourEmployerOrTheGovernment',
        label: 'Were the benefits provided by your employer or the government?',
        type: 'select',
        options: [
          { label: 'Employer', value: 'Employer' },
          { label: 'US Federal Government', value: 'US Federal Government' },
          {
            label: 'US State/Local Government',
            value: 'US State/Local Government',
          },
          { label: 'Non-US Government', value: 'Non-US Government' },
        ],
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },

  {
    name: 'doYouHaveACancelledieForgivenDuringTheYearDebtThatYouDidNotPayBack',
    label:
      'Do you have a cancelled (ie forgiven during the year) debt that you did not pay back?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadForm1099C',
        label: 'Please upload form 1099C',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'didYouFileBankruptcyAnyTimeInThePast',
        label: 'Did you file bankruptcy any time in the past?',
        type: 'radio',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'pleaseProvideDetails',
            label: 'Please provide details',
            type: 'text',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'yes',
            placeholder: 'Enter',
          },
        ],
      },
    ],
  },

  {
    name: 'didYouHaveLotterygamblingWinningOrLawsuitSettlementDuringTheFilingYear',
    label:
      'Did you have Lottery/Gambling winning or Lawsuit Settlement during the filing year?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadSupportingDocumentsdidYouHaveLottery',
        label: 'Please upload supporting documents',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'pleaseProvideMoreDetailsdidYouHaveLottery',
        label: 'Please provide details',
        type: 'text',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Enter',
      },
    ],
  },
  {
    dependenciesTitle: 'Non-Taxable Income',
    name: 'didYouReceiveAnyEconomicImpactPaymentsLastYearcoronavirusStimulusPayment',
    label:
      'Did you receive any Economic Impact Payments Last year? (Coronavirus stimulus payment)',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'didYouReceiveTheFirstStimulusOrBothStimulusPayments',
        label: 'Did you receive the first stimulus, or both stimulus payments?',
        subLabel: () => <p>$1200 per person, then $600 per person</p>,
        type: 'select',
        options: [
          {
            label: 'First stimulus check for $1200',
            value: 'First stimulus check for $1200',
          },
          {
            label: 'Second stimulus check for $600',
            value: 'Second stimulus check for $600',
          },
          {
            label: 'I received both stimulus checks',
            value: 'I received both stimulus checks',
          },
        ],
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'firstGovernmentStimulusPackage',
            label: 'First government stimulus package',
            subLabel: () => (
              <p>
                Please Upload Notice 1444 (preferred)If you don't have this -
                it's ok. Please make sure to complete the amount below.
              </p>
            ),
            type: 'upload',
            predicate: (value) =>
              value === 'First stimulus check for $1200' ||
              value === 'I received both stimulus checks',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'pleaseProvideTheAmountReceivedFromTheCoronavirusStimulusPayments',
            label:
              'Please provide the amount received from the coronavirus stimulus payments',
            type: 'currency',
            predicate: (value) =>
              value === 'First stimulus check for $1200' ||
              value === 'I received both stimulus checks',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'secondGovernmentStimulusPackage',
            label: 'Second government stimulus package',
            subLabel: () => (
              <p>
                Please Upload Notice 1444 (preferred) If you don't have this -
                it's ok. Please make sure to complete the amount below.
              </p>
            ),
            type: 'upload',
            predicate: (value) =>
              value === 'Second stimulus check for $600' ||
              value === 'I received both stimulus checks',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'pleaseProvideTheAmountReceivedFromTheCoronavirusStimulusPayments',
            label:
              'Please provide the amount received from the coronavirus stimulus payments',
            type: 'currency',
            predicate: (value) =>
              value === 'Second stimulus check for $600' ||
              value === 'I received both stimulus checks',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
        ],
      },
    ],
  },

  {
    name: 'haveYouReceivedChildBenefitsOfAnyKind',
    label: 'Have you received child benefits of any kind?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseDescribeTheBenefitsYouveReceived',
        label: "Please describe the benefits you've received",
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Enter',
      },
      {
        name: 'wereTheBenefitsProvidedByYourEmployerOrTheGovernmenthaveYouReceivedChildBenefitsOfAnyKind',
        label: 'Were the benefits provided by your employer or the government?',
        type: 'select',
        options: [
          { label: 'Employer', value: 'Employer' },
          { label: 'US Federal Government', value: 'US Federal Government' },
          {
            label: 'US State/Local Government',
            value: 'US State/Local Government',
          },
          { label: 'Non-US Government', value: 'Non-US Government' },
        ],
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },

  {
    name: 'haveYouRecievedWelfareBenefits',
    label: 'Have you recieved welfare benefits?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'welfareBenefitesThisIsNotUnemploymentInsurance',
        label: 'Welfare benefites ( this is NOT unemployment insurance)',
        subLabel: () => (
          <p>
            Does not refer to unemployment insurance. Ex.: in Germany
            Sozialleistungen
          </p>
        ),
        type: 'currency',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'haveYouReceivedGovernmentHousingSubsidies',
    label: 'Have you received government housing subsidies?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'governmentHousingSubsidy',
        label: 'Government housing subsidy',
        subLabel: () => (
          <p>
            Actual cash you receive from the government to pay for housing. Ex:
            Germany - Wohngeld
          </p>
        ),
        type: 'currency',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
];

export const validationSchema = Yup.object().shape({});
